import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, DoorClosed, Loader2, FileText, Receipt,
  ChevronDown, ChevronRight, Eye, ExternalLink, Mail, Phone, Users,
} from "lucide-react";
import { clientsApi, savedReportsApi, quotesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError, fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function base64ToBlobUrl(b64) {
  const clean = String(b64).replace(/^data:application\/pdf;base64,/, "");
  const chars = atob(clean);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

function fmtMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `£${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function BuildingBlock({ building, onOpenBuilding }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quotesHidden, setQuotesHidden] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rRes] = await Promise.all([
        savedReportsApi.list({ building_id: building.id }),
      ]);
      setReports(Array.isArray(rRes.data) ? rRes.data : []);
      try {
        const qRes = await quotesApi.list({ building_id: building.id });
        setQuotes(Array.isArray(qRes.data) ? qRes.data : []);
      } catch (e) {
        if (e?.response?.status === 403) setQuotesHidden(true);
        else throw e;
      }
      setLoaded(true);
    } catch (e) {
      toast.error(apiError(e, "Could not load building records"));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) loadData();
  };

  const viewReport = async (r) => {
    setBusy(`r-${r.id}`);
    try {
      const res = await savedReportsApi.download(r.id);
      const { pdf_base64 } = res.data || {};
      if (!pdf_base64) throw new Error("This report has no PDF stored.");
      window.open(base64ToBlobUrl(pdf_base64), "_blank", "noopener");
    } catch (e) {
      toast.error(apiError(e, "Could not open report"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`client-building-${building.id}`}>
      <button
        className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-slate-50"
        onClick={toggle}
        data-testid={`client-building-toggle-${building.id}`}
      >
        <div className="flex min-w-0 items-center gap-3">
          {open ? <ChevronDown className="h-5 w-5 shrink-0 text-slate-400" /> : <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Building2 className="h-5 w-5" /></div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-800">{building.name}</p>
            <p className="truncate text-sm text-slate-500">{[building.address, building.postcode].filter(Boolean).join(", ") || "—"}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {building.assignment === "whole"
            ? <Badge variant="outline" className="gap-1 bg-blue-50 text-blue-700"><Users className="h-3 w-3" /> Whole building</Badge>
            : <Badge variant="outline" className="gap-1 bg-amber-50 text-amber-700"><DoorClosed className="h-3 w-3" /> {building.client_door_count} of {building.door_count} doors</Badge>}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 p-4" data-testid={`client-building-detail-${building.id}`}>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpenBuilding(building.id)} data-testid={`open-building-${building.id}`}>
              <ExternalLink className="mr-1.5 h-4 w-4" /> Open building
            </Button>
            <Badge variant="outline" className="gap-1 text-slate-500"><DoorClosed className="h-3 w-3" /> {building.door_count} doors</Badge>
          </div>

          {loading ? (
            <LoadingState label="Loading records…" />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Reports */}
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><FileText className="h-4 w-4 text-slate-400" /> Reports ({reports.length})</p>
                {reports.length === 0 ? (
                  <p className="rounded-lg bg-white px-3 py-2 text-sm text-slate-400 ring-1 ring-slate-200" data-testid={`no-reports-${building.id}`}>No saved reports.</p>
                ) : (
                  <div className="space-y-1.5" data-testid={`reports-list-${building.id}`}>
                    {reports.map((r) => (
                      <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-700">{r.title}</p>
                          <p className="truncate text-xs text-slate-400">{prettyStatus(r.report_type || "")} · {fmtDate(r.generated_at)}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {r.verification_status && <Badge variant="outline" className={`${statusTone(r.verification_status)} hidden sm:inline-flex`}>{prettyStatus(r.verification_status)}</Badge>}
                          <Button size="sm" variant="ghost" onClick={() => viewReport(r)} disabled={busy === `r-${r.id}`} data-testid={`view-report-${r.id}`}>
                            {busy === `r-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quotes */}
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Receipt className="h-4 w-4 text-slate-400" /> Quotes sent ({quotesHidden ? "—" : quotes.length})</p>
                {quotesHidden ? (
                  <p className="rounded-lg bg-white px-3 py-2 text-sm text-slate-400 ring-1 ring-slate-200">Quotes are hidden for your role.</p>
                ) : quotes.length === 0 ? (
                  <p className="rounded-lg bg-white px-3 py-2 text-sm text-slate-400 ring-1 ring-slate-200" data-testid={`no-quotes-${building.id}`}>No quotes sent.</p>
                ) : (
                  <div className="space-y-1.5" data-testid={`quotes-list-${building.id}`}>
                    {quotes.map((q) => (
                      <button
                        key={q.id}
                        className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-left ring-1 ring-slate-200 hover:ring-primary/40"
                        onClick={() => navigate(`/quotes/${q.id}`)}
                        data-testid={`open-quote-${q.id}`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-700">{q.title || "Quote"}</p>
                          <p className="truncate text-xs text-slate-400">{fmtMoney(q.total)} · {fmtDate(q.created_at)}</p>
                        </div>
                        {q.status && <Badge variant="outline" className={statusTone(q.status)}>{prettyStatus(q.status)}</Badge>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useAsync(() => clientsApi.get(id), [id]);
  const buildings = useAsync(() => clientsApi.buildings(id), [id]);

  const c = client.data;
  const list = Array.isArray(buildings.data) ? buildings.data : [];

  return (
    <div>
      <button className="mb-3 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" onClick={() => navigate("/clients")} data-testid="client-detail-back">
        <ArrowLeft className="h-4 w-4" /> Clients
      </button>

      {client.loading && <LoadingState label="Loading client…" />}
      {!client.loading && client.error && <ErrorState message={client.error} onRetry={client.reload} />}

      {!client.loading && !client.error && c && (
        <>
          <PageHeader
            title={c.name}
            subtitle={[c.contact_name, c.contact_email, c.contact_phone].filter(Boolean).join(" · ") || "Client overview"}
            testId="client-detail-header"
            actions={
              <Button variant="outline" onClick={() => navigate(`/invoices?client=${c.id}&name=${encodeURIComponent(c.name)}`)} data-testid="client-detail-invoices">
                <Receipt className="mr-1.5 h-4 w-4" /> Invoices
              </Button>
            }
          />

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="gap-1 text-slate-500"><Building2 className="h-3 w-3" /> {list.length} building{list.length === 1 ? "" : "s"}</Badge>
            {c.contact_email && <Badge variant="outline" className="gap-1 text-slate-500"><Mail className="h-3 w-3" /> {c.contact_email}</Badge>}
            {c.contact_phone && <Badge variant="outline" className="gap-1 text-slate-500"><Phone className="h-3 w-3" /> {c.contact_phone}</Badge>}
          </div>

          {buildings.loading ? (
            <LoadingState label="Loading buildings…" />
          ) : buildings.error ? (
            <ErrorState message={buildings.error} onRetry={buildings.reload} />
          ) : list.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No buildings assigned"
              message="Assign this client to a building (or to individual doors within a building) and it will appear here with its reports and quotes."
              testId="client-detail-empty"
            />
          ) : (
            <div className="space-y-3" data-testid="client-buildings-list">
              {list.map((b) => (
                <BuildingBlock key={b.id} building={b} onOpenBuilding={(bid) => navigate(`/buildings/${bid}`)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
